import { useEffect, useState } from 'react';
import { getMe, getProducts } from '@/lib/api';
import type { Product as ProductType } from '@/types/index';
import { SPECIAL_USERS } from '@/util/utils';

interface UseProductsReturn {
  products: ProductType[];
  groupedProducts: Array<{
    promotion: ProductType['promotion'];
    products: ProductType[];
  }>;
  isSpecialUser: boolean;
  isLoading: boolean;
}

export function useProducts(): UseProductsReturn {
  const [products, setProducts] = useState<ProductType[]>([]);
  const [groupedProducts, setGroupedProducts] = useState<
    Array<{
      promotion: ProductType['promotion'];
      products: ProductType[];
    }>
  >([]);
  const [isSpecialUser, setIsSpecialUser] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const products = await getProducts();
        const me = await getMe({ forceRefresh: true });
        if (me?.email && SPECIAL_USERS.includes(me.email)) {
          setIsSpecialUser(true);
          // Group products by promotion.id
          const groupsMap = new Map<
            number,
            { promotion: ProductType['promotion']; products: ProductType[] }
          >();
          (products || []).forEach((product: ProductType) => {
            if (!product.promotion) return;
            if (!groupsMap.has(product.promotion.id)) {
              groupsMap.set(product.promotion.id, {
                promotion: product.promotion,
                products: [],
              });
            }
            groupsMap.get(product.promotion.id)?.products.push(product);
          });
          setGroupedProducts(Array.from(groupsMap.values()));
        } else {
          setIsSpecialUser(false);
          setProducts(products || []);
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
        setProducts([]);
        setGroupedProducts([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProducts();
  }, []);

  return {
    products,
    groupedProducts,
    isSpecialUser,
    isLoading,
  };
}
